import React, { useMemo, useState } from 'react';

import Button from '@creditflowresearch/uilib/Button';
import type { ButtonVariant, ButtonSize } from '@creditflowresearch/uilib/Button';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import CheckUserAccess from '@/creditflowresearch/ui/common/components/CheckUserAccess';
import { RestrictedAccessExplanation } from '@/creditflowresearch/ui/common/components/Explanations';
import { mapUserPlanToSubscribeDescription } from '@/creditflowresearch/application/User/UserPlan/initialState';
import {
  RestrictedButtonType,
} from '@/creditflowresearch/ui/common/components/Explanations/RestrictedAccessExplanation/RestrictedAccessExplanation';
import IconEmailChecked from '@creditflowresearch/uilib/Icon/IconEmailChecked';
import IconEmailAlerts from '@creditflowresearch/uilib/Icon/IconEmailAlerts';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';

import styles from './subscribeButton.module.scss';

interface ISubscribeButtonPremiumizationProps {
  requiredPlan: UserPlanType | string,
  label: string,
  onClick: () => void,
  isSubscribed: boolean,
  isFetching: boolean,
  restrictedButtonType: RestrictedButtonType,
  variant?: ButtonVariant,
  size?: ButtonSize,
  checks?: boolean[],
  /** @deprecated Temporary solution until new design is applied everywhere */
  isLegacy?: boolean,
}

const SubscribeButtonPremiumization = ({
  requiredPlan,
  label,
  onClick,
  isSubscribed,
  isFetching,
  restrictedButtonType,
  variant = 'outline',
  size = 'S',
  checks = [],
  isLegacy = false,
}: ISubscribeButtonPremiumizationProps) => {
  const [isExplanationOpen, setIsExplanationOpen] = useState<boolean>(false);
  const { checkAccessByUserPlan } = useUserPlanContext();

  const icon = isSubscribed
    ? <IconEmailChecked />
    : <IconEmailAlerts />;

  const buttonClassName = isLegacy ? styles.legacyButton : undefined;
  const resolvedIconSize = isLegacy ? 16 : undefined;

  const description = useMemo<string>(() => (
    `Email digests are available on ${ mapUserPlanToSubscribeDescription[requiredPlan] }.
    ${ restrictedButtonType === RestrictedButtonType.LogIn ? 'Log in for access.' : 'Upgrade your plan for access.' }`
  ), [requiredPlan, restrictedButtonType]);

  return (
    <CheckUserAccess
      conditions={ [{
        checks: [checkAccessByUserPlan(requiredPlan as UserPlanType), ...checks],
        fallback: (
          <RestrictedAccessExplanation
            title={ label }
            description={ description }
            closeTooltip={ () => {
              setIsExplanationOpen(false);
            } }
            isOpen={ isExplanationOpen }
            actionButtonType={ restrictedButtonType }
          >
            <Button
              dataTest="subscribeButton"
              variant={ variant }
              size={ size }
              icon={ icon }
              iconSize={ resolvedIconSize }
              ariaLabel={ label }
              onClick={ () => setIsExplanationOpen(true) }
              className={ buttonClassName }
            />
          </RestrictedAccessExplanation>
        ),
      }] }
    >
      <Tooltip content={ label }>
        <Button
          dataTest="subscribeButton"
          variant={ variant }
          size={ size }
          icon={ icon }
          iconSize={ resolvedIconSize }
          ariaLabel={ label }
          onClick={ onClick }
          disabled={ isSubscribed }
          loading={ isFetching }
          className={ buttonClassName }
        />
      </Tooltip>
    </CheckUserAccess>
  );
};

export default SubscribeButtonPremiumization;
